import { ref, computed } from 'vue';
import { useSdk } from './useSdk';
import type { ActingContext } from '@cc-consulting-nv/ccsdk';

const actingContext = ref<ActingContext | null>(null);
const currentTime = ref(Date.now());
let intervalId: number | null = null;

/**
 * Composable for managing acting context (delegated user access)
 * Provides reactive state synchronized with the SDK
 */
export function useActingContext() {
  const { sdk } = useSdk();
  const isActing = computed(() => {
    if (!actingContext.value) return false;

    // Check if token has expired
    const expiresAt = new Date(actingContext.value.expiresAt).getTime();
    if (currentTime.value >= expiresAt) {
      // Token expired, clear it
      clearActingContext();
      return false;
    }

    return true;
  });

  const remainingTime = computed(() => {
    if (!actingContext.value) return '';

    const expiresAt = new Date(actingContext.value.expiresAt).getTime();
    const diff = expiresAt - currentTime.value;

    if (diff <= 0) return 'Expired';

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds} second${seconds !== 1 ? 's' : ''}`;
    }

    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  });

  function loadActingContext() {
    if (!sdk) return;

    const context = sdk.getActingContext();
    actingContext.value = context;
  }

  function setActingContext(context: ActingContext) {
    if (!sdk) return;

    sdk.setActingContext(context);
    actingContext.value = context;
  }

  function clearActingContext() {
    if (!sdk) return;

    sdk.clearActingContext();
    actingContext.value = null;
  }

  function exitActingMode() {
    clearActingContext();
    // Reload the page to clear any acting state
    window.location.reload();
  }

  function startTimer() {
    if (intervalId !== null) return; // Already running

    // Update current time every second
    intervalId = window.setInterval(() => {
      currentTime.value = Date.now();
    }, 1000);
  }

  function stopTimer() {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  // Initialize on first use
  if (actingContext.value === null) {
    loadActingContext();
  }

  return {
    actingContext: computed(() => actingContext.value),
    isActing,
    remainingTime,
    loadActingContext,
    setActingContext,
    clearActingContext,
    exitActingMode,
    startTimer,
    stopTimer,
  };
}
